import * as Yup from 'yup';
import moment from 'moment';

import { Nullable } from '~globals/types';
import {
  IntegrationProvidersTypes,
  IntegrationDeliveryTypes,
} from '~globals/types/enums';
import { VALIDATIONS_ERROR } from '~constants/index';
import { AdminIntegration } from '~services/admin/integration/types';

export const INTEGRATION_ID_KEY = 'companyIntegrationId';

export const FIELDS_NAME = {
  DATE_FROM: 'dateFrom',
  DATE_TO: 'dateTo',
  ZONES_ID: 'zonesId',
  INTEGRATIONS: 'integrations',
  INTEGRATION_CHECKED: 'integrationChecked',
  INTEGRATION_ID: 'integrationId',
  INTEGRATION_TYPE: 'integrationType',
  INTEGRATION_DELIVERY_TYPE: 'integrationDeliveryType',
  ONLY_TODAY_DELIVERY: 'onlyTodayDelivery',
} as const;

const defaultDateFrom = moment().startOf('day');
const defaultDateTo = moment().endOf('day');

export const isMeliIntegration = (
  integrationTypeId: IntegrationProvidersTypes,
): boolean => integrationTypeId === IntegrationProvidersTypes.MercadoLibre;

const getDefaultDeliveryTypeByIntegrationType = (
  integrationTypeId: IntegrationProvidersTypes,
): IntegrationDeliveryTypes[] =>
  isMeliIntegration(integrationTypeId)
    ? [IntegrationDeliveryTypes.SelfService]
    : [];

const getDefaultIntegrationsDataForm = (
  currentIntegrationsData: AdminIntegration[],
) =>
  currentIntegrationsData.map((integration) => ({
    [FIELDS_NAME.INTEGRATION_CHECKED]: true,
    [FIELDS_NAME.INTEGRATION_ID]: integration[INTEGRATION_ID_KEY],
    [FIELDS_NAME.INTEGRATION_TYPE]: integration.integrationTypeId,
    [FIELDS_NAME.INTEGRATION_DELIVERY_TYPE]:
      getDefaultDeliveryTypeByIntegrationType(integration.integrationTypeId),
  }));

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getInitialValues = (
  currentIntegrationsData: AdminIntegration[],
) => ({
  [FIELDS_NAME.DATE_FROM]: defaultDateFrom.toDate() as Nullable<Date>,
  [FIELDS_NAME.DATE_TO]: defaultDateTo.toDate() as Nullable<Date>,
  [FIELDS_NAME.ZONES_ID]: null as Nullable<string[]>,
  [FIELDS_NAME.INTEGRATIONS]: getDefaultIntegrationsDataForm(
    currentIntegrationsData,
  ),
  [FIELDS_NAME.ONLY_TODAY_DELIVERY]: false,
});

const validateDateSchema = Yup.date()
  .nullable()
  .typeError(VALIDATIONS_ERROR.INVALID_DATE)
  .required(VALIDATIONS_ERROR.REQUIRED);

const valiateIntegrationDeliveryType = Yup.array()
  .of(Yup.string())
  .when([FIELDS_NAME.INTEGRATION_CHECKED, FIELDS_NAME.INTEGRATION_TYPE], {
    is: (checked: boolean, integrationTypeId: IntegrationProvidersTypes) =>
      checked && isMeliIntegration(integrationTypeId),
    then: (schema) => schema.min(1, VALIDATIONS_ERROR.REQUIRED),
  });

const validateIntergrationsSchema = Yup.array()
  .of(
    Yup.object().shape({
      [FIELDS_NAME.INTEGRATION_CHECKED]: Yup.boolean(),
      [FIELDS_NAME.INTEGRATION_ID]: Yup.string().required(),
      [FIELDS_NAME.INTEGRATION_TYPE]: Yup.number().required(),
      [FIELDS_NAME.INTEGRATION_DELIVERY_TYPE]: valiateIntegrationDeliveryType,
    }),
  )
  .min(1, VALIDATIONS_ERROR.REQUIRED);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const validationSchema = Yup.object().shape({
  [FIELDS_NAME.DATE_FROM]: validateDateSchema,
  [FIELDS_NAME.DATE_TO]: validateDateSchema,
  [FIELDS_NAME.ZONES_ID]: Yup.array().of(Yup.string()).nullable(),
  [FIELDS_NAME.INTEGRATIONS]: validateIntergrationsSchema,
  [FIELDS_NAME.ONLY_TODAY_DELIVERY]: Yup.boolean(),
});

export const toIntegrationPathField = (
  index: number,
  keyField: string,
): string => `${FIELDS_NAME.INTEGRATIONS}[${index}][${keyField}]`;
